import React, {
  useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore,
} from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReplayIcon from '@mui/icons-material/Replay';
import CloseIcon from '@mui/icons-material/Close';

import { Check, AlertTriangle } from 'lucide-react';
import { getScraperSSEToken } from '../api/scraperService';
import { useScrapeStream } from '../hooks/useScrapeStream';
import ProgressStream from '../components/common/ProgressStream';
import PageContainer from '../components/common/PageContainer';
import Pill from '../components/common/Pill';
import ViewHeader from '../components/ViewHeader';
import WeekCalendarView from '../WeekCalendarView';
import PricingAutomationDashboard from '../components/PricingAutomationDashboard';
import ManualDriftCard from '../components/ManualDriftCard';
import FulfillmentCostKpiCard from '../components/fulfillment/FulfillmentCostKpiCard';
import { HOME_DASHBOARD_TOOLTIPS } from '../components/fulfillment/fulfillmentCostTooltips';
import Panel from '../components/common/Panel';
import EmptyState from '../components/common/EmptyState';
import { BUTTON_RADIUS, SP, hoverOnly } from '../theme/tokens';
import UrgencyRow from './components/UrgencyRow';
import MarketIntelligenceWidget from './components/MarketIntelligenceWidget';
import { formatDateTime, formatMoney, formatPercent } from '../utils/format';
import DisabledReason from '../components/common/DisabledReason';
// UI2 Teil 9, Karte Z-1c: nach der Erholung laden die Regionen selbst neu.
import useReloadOnRecovery from '../serviceOutage/useReloadOnRecovery';
// Und der rote Klartext unten schweigt im Ausfall wie ein `MuiAlert` unter E-3.
import { abonniere, aktuellerZustand, ZUSTAND } from '../api/serviceOutage';


const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
const withApiBase = (path) => `${API_BASE}${path}`;

/**
 * Die Frische-Zeile der Kopfzeile (W-33/D1).
 *
 * 🔴 Kein "auto-refresh 5 min", obwohl das Konzept genau das vorschlaegt: diese
 * Seite laedt ihre sieben Regionen EINMAL beim Aufbau und danach nur auf Klick
 * (`handleRefresh` → `refreshTick`). Der einzige Takt auf dieser Seite ist die
 * Statusabfrage des Scrapers alle 5 s (`pollScraperStatus`) — sie frischt keine
 * einzige Zahl auf dem Brett auf. Eine zugesagte Aktualisierung, die es nicht
 * gibt, ist schlimmer als keine Zusage.
 */
const fmtDataAsOf = (date) => (date
  ? `Data as of ${formatDateTime(date)}`
  : 'Loading…');

const money = (currency, value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '–';
  try {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency || 'EUR',
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return formatMoney(amount, { currency: currency || 'EUR' });
  }
};


const formatRelativeHours = (value) => {
  if (!value) return 'never';
  const d = new Date(value);
  const diff = Date.now() - d.getTime();
  const hours = Math.round(diff / (1000 * 60 * 60));
  if (hours < 1) return 'just now';
  if (hours === 1) return '1 hour ago';
  if (hours < 24) return `${hours} hours ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? '1 day ago' : `${days} days ago`;
};

const todayKey = () => {
  const now = new Date();
  return `home-done-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
};


const readPersistedDone = () => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(todayKey());
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
};

const persistDone = (map) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(todayKey(), JSON.stringify(map));
  } catch {
    // ignore quota errors
  }
};

const STALE_PAGE_SIZE = 8;
const BATCH_REFRESH_SYNC_ID = 'stale-batch-refresh';

// ─── Regionen des Dashboards ────────────────────────────────────────────────
// Jede Region holt ihre Daten selbst und traegt ihren eigenen Fehler.
//
// 🔴 Anlass (W-07): hier stand ein EINZIGES `Promise.all` ueber sieben Abrufe.
// `Promise.all` verwirft ALLE Ergebnisse, sobald EINES abgelehnt wird — die
// sechs geglueckten Abrufe waren bezahlt und weggeworfen, und das Brett zeigte
// einen globalen Fehlerbalken statt sechs funktionierender Flaechen. Schlimmer
// als der Balken waren die zwei Leerzustaende darunter: sie behaupteten
// "Nothing scheduled for this week" und "Storage data pending", wo in Wahrheit
// ein Netzfehler stand. Ein Ausfall sah aus wie "keine Arbeit".
//
// Die Schluessel sind zugleich die Namen in `regionErrors`.
const REGION_ENDPOINTS = {
  kpis:     '/api/dashboard/kpis',
  reorder:  '/api/dashboard/reorder-alerts',
  incoming: '/api/dashboard/incoming',
  storage:  '/api/dashboard/storage-summary',
  rma:      '/api/rma/stats',
  lastRun:  '/api/scrapers/last-run',
};


// ─── Blog Topic Banner ────────────────────────────────────────────────────────
// Dismissal resets each Monday (ISO week) — reminder cadence: once per week.
function getBlogBannerKey() {
  const d = new Date();
  const day = d.getDay(); // 0=Sun, 1=Mon…
  const monday = new Date(d);
  monday.setDate(d.getDate() - ((day + 6) % 7)); // roll back to Monday
  return `pog-blog-banner-dismissed-${monday.toISOString().slice(0, 10)}`;
}

function BlogBanner({ apiBase }) {
  const [stats, setStats] = useState(null);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(getBlogBannerKey()) === '1'
  );

  useEffect(() => {
    if (dismissed) return;
    axios.get(`${apiBase}/api/admin/blog/topics/stats`)
      .then(r => setStats(r.data))
      .catch(() => {});
  }, [apiBase, dismissed]);

  const discovered = stats?.discovered ?? 0;
  const highQuality = stats?.high_quality ?? 0;

  if (dismissed || !stats || discovered === 0) return null;

  const msg = highQuality > 0
    ? `${highQuality} high-quality topic${highQuality !== 1 ? 's' : ''} ready to review — approve or discard before next week's scan.`
    : `${discovered} new topic${discovered !== 1 ? 's' : ''} discovered this week. Check the inbox for anything worth approving.`;

  return (
    <Alert
      severity="info"
      sx={{ mb: 2 }}
      action={
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            variant="outlined"
            component={RouterLink}
            to="/blog-sources?tab=1"
            sx={{ whiteSpace: 'nowrap' }}
          >
            Review topics
          </Button>
          <IconButton
            size="small"
            aria-label="Dismiss blog topics banner"
            onClick={() => {
              localStorage.setItem(getBlogBannerKey(), '1');
              setDismissed(true);
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      }
    >
      {msg}
    </Alert>
  );
}

export default function HomeDashboard() {
  // 🔴 UI2 Teil 9, Karte Z-1c (E-5): den Erreichbarkeits-Zustand ABONNIEREN, nicht
  // lesen. `istAusfall()` waere ein Renderzeit-Entscheid ohne Abonnement — der
  // Provider wird in `App.js` nur um den Banner montiert, eine Zustandsaenderung
  // rendert also nur den Banner neu. In der NATUERLICHEN Reihenfolge (Abruf
  // scheitert → Flaeche rendert ihren Fehler → DANN bestaetigt die Sonde den
  // Ausfall) bliebe der rote Klartext damit stehen. Genau dieselbe Form wie
  // `EmptyState.jsx`; der Modulweg ist noetig, weil Home unter `<Routes>` ohne
  // Provider-Vorfahr liegt und `useServiceOutage()` dort dauerhaft `online` saehe.
  const zustand = useSyncExternalStore(abonniere, aktuellerZustand, aktuellerZustand);
  const imAusfall = zustand === ZUSTAND.AUSFALL;

  const [kpis, setKpis] = useState(null);
  const [rmaStats, setRmaStats] = useState(null);
  const [reorderTasks, setReorderTasks] = useState([]);
  const [incomingItems, setIncomingItems] = useState([]);
  const [staleTargets, setStaleTargets] = useState([]);
  const [staleTotal, setStaleTotal] = useState(0);
  const [stalePage, setStalePage] = useState(0);
  const [staleLoading, setStaleLoading] = useState(false);
  const [batchRefreshPending, setBatchRefreshPending] = useState(false);
  const [scraperRunStatus, setScraperRunStatus] = useState(null);
  const [storageSummary, setStorageSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Fehlerhoheit liegt bei der Region, nicht beim Brett: `{ [Region]: true }`
  // fuer jede Region, deren Abruf gescheitert ist (W-07). Leer heisst "alles da".
  const [regionErrors, setRegionErrors] = useState({});
  const [refreshTick, setRefreshTick] = useState(0);
  const [doneMap] = useState(readPersistedDone);
  const [showDone, setShowDone] = useState(false);
  // 🔴 W-33/D1: der Zeitpunkt des letzten geglueckten Ladens. Die Unterzeile
  // des Kopfes nennt ihn — "Data as of" ist die Frage, die diese Seite vorher
  // nicht beantworten konnte: wie alt sind die Zahlen, die hier stehen?
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const [activeScrapeTargetId, setActiveScrapeTargetId] = useState(null);
  const scrape = useScrapeStream();
  const [targetMenuAnchor, setTargetMenuAnchor] = useState(null);
  const [targetMenuTarget, setTargetMenuTarget] = useState(null);
  const [batchResult, setBatchResult] = useState(null);   // { succeeded, failed, total } after run
  const [scrapeError, setScrapeError] = useState(null);   // section-level error string
  const [scraperExpanded, setScraperExpanded] = useState(true);
  const [scraperLastRun, setScraperLastRun] = useState(null);
  const syncEmittedRef = useRef(false);
  const syncGraceUntilRef = useRef(0);
  // Synchronous re-entrance guard — set before any await so it's effective
  // before React propagates batchRefreshPending state to the next render.
  const batchRefreshLockRef = useRef(false);
  const prevBatchRunningRef = useRef(false);
  const navigate = useNavigate();
  const batchRefreshRunning = batchRefreshPending || Boolean(scraperRunStatus?.running);
  useEffect(() => {
    persistDone(doneMap);
  }, [doneMap]);

  // 🔴 W-33/D7 (2026-09-28): hier standen die zweiseitige `HeroSearch` dieser
  // Seite, ihr Zustand und dieser verzoegerte Abruf auf `POST /api/search` —
  // zusammen mit einem Effekt, der die Kopfzeilensuche dieser Seite ABSCHALTETE
  // (`showSearch: false`), damit nicht zwei Eingaben gleichzeitig auf dem Schirm
  // stehen.
  //
  // Der Grund fuer die Abschaltung ist entfallen, und zwar messbar: der
  // Shell-Effekt, der `headerConfig` bei jedem Routenwechsel zuruecksetzte, lief
  // VOR dem Effekt der Seite und loeschte ihr `showSearch: false` — die
  // Kopfzeilensuche war auf dem Dashboard also die ganze Zeit SICHTBAR, und die
  // Seite hatte zwei Eingaben (der Befund des Konzepts, `x-dashboard-mid.png`).
  // W-11 hat den Reset waehrend des Renderns verlegt (`SidebarAndHeaderBar.jsx`
  // `resetForPath`), seitdem greift `showSearch: false` wirklich — das Dashboard
  // hatte also genau eine Eingabe, aber die ZWEITE Implementierung derselben
  // Suche. Beide fragen `POST /api/search` mit `{ q }`.
  //
  // Entfernt ist deshalb die Seitenkopie, nicht die Kopfzeilensuche: die
  // globale Suche kennt zusaetzlich die letzten Suchbegriffe, hat dieselbe
  // Rennschranke und steht auf jeder anderen Seite an derselben Stelle.

  // 🔴 W-73 (2026-09-28): hier stand die Preis-Kette — `isDone`, `undonePrice`,
  // `priceTasks`, `setPricePage`, `PRICE_PAGE_SIZE` und ein Effekt, der einen
  // Zaehler klemmte. Sie hing an einem eigenen Abruf
  // (`/api/dashboard/price-recommendations`), und ihr Ergebnis wurde NIE
  // gelesen: `setPricePage` kam aus `const [, setPricePage] = useState(0)`,
  // die erste Haelfte der Destrukturierung ist der verworfene Wert. Ein
  // vollstaendiger Netzaufruf pro Dashboard-Last fuer nichts.
  //
  // Entfernt statt umgestellt: ein toter Rechenweg ohne Leser hat keinen
  // Sollwert, an dem man ihn ausrichten koennte. `doneMap` bleibt — die
  // Stale-Kette liest es weiter ueber `doneEntries`.
  // `null` = the fetch failed. `null > 0` is false, so the all-clear below would
  // still have rendered without this second flag — the unknown case needs its own.
  const staleKnown = staleTotal !== null;
  const hasStaleTargets = staleKnown && staleTotal > 0;
  const errorTargets = staleTargets.filter(t => (t.failed_scrapes_in_a_row || 0) > 0);
  const staleHealthyTargets = staleTargets.filter(t => (t.failed_scrapes_in_a_row || 0) === 0);
  // Welche der KPI-Quellen ausgefallen sind. `storage` fehlt hier bewusst: das
  // Lager hat mit "Storage and inventory" eine eigene Flaeche, und zwei
  // Meldungen fuer denselben Abruf waeren eine zu viel.
  const kpiErrorSources = ['kpis', 'rma'].filter((key) => regionErrors[key]);
  // Der Kalender braucht BEIDE Zufuhren. Faellt eine aus, zeigt er weniger
  // Termine als es gibt — und das ist genau die Sorte Luege, die der
  // Leerzustand mit `reason` verhindern soll.
  const calendarErrorSources = ['incoming', 'reorder'].filter((key) => regionErrors[key]);
  const doneEntries = useMemo(
    () =>
      Object.entries(doneMap || {}).map(([key, meta]) => ({
        key,
        label: meta?.label || key,
        completed_at: meta?.completed_at,
      })),
    [doneMap],
  );

  const groupedDoneEntries = useMemo(() => {
    const map = new Map();
    doneEntries.forEach((entry) => {
      const type = entry.key.split(':')[0] || 'misc';
      const group = map.get(type) || [];
      group.push(entry);
      map.set(type, group);
    });
    return map;
  }, [doneEntries]);

  const calendarIncomingEvents = useMemo(
    () =>
      incomingItems
        .filter((item) => item.eta)
        .map((item) => ({ date: new Date(item.eta), resource: item })),
    [incomingItems]
  );

  const calendarReorderEvents = useMemo(
    () =>
      reorderTasks
        .map((task) => ({
          date: task.reorder_date ? new Date(task.reorder_date) : task.estimated_stockout_date ? new Date(task.estimated_stockout_date) : null,
          resource: task,
        }))
        .filter((evt) => evt.date),
    [reorderTasks]
  );

  // 🔴 Lauf-Wache (Nachbesserung 1, 2026-10-11). `fetchData` laeuft beim
  // Einhaengen UND bei jeder Erholung; die Laeufe koennen sich ueberholen. Der
  // aeltere ist dabei der LAENGERE — seine Anfragen liegen hinter der
  // asynchronen Anfragekette (`index.js:65` holt erst das Token), im Betrieb
  // kommt die echte Netzlaufzeit dazu. Ohne Wache schreibt der ueberholte Lauf
  // sein `failed` NACH dem neueren und setzt `regionErrors` auf einen Stand
  // zurueck, den der neuere gerade weggeraeumt hat. Nur der JUENGSTE Lauf
  // schreibt Zustand.
  //
  // 🔴 ZUSCHREIBUNG, berichtigt in Nachbesserung 2 (2026-10-11). Hier stand bis
  // dahin, „genau das" sei der Z1.8-Befund am Kopf `2591d753904` gewesen. Das
  // ist WIDERLEGT: die Browser-Messung des Orchestrators hat den roten
  // Z1.8-Schritt auf der PREIS-AUTOMATION-Karte gefunden
  // (`PricingAutomationDashboard.jsx`, `LastRunPanel`, Text „The last run could
  // not be loaded."). Der Home-Klartext war im Ausfall gar nicht im Baum (E-5
  // wirkt), und `getByText` fasst mit einer Teilzeichenkette beide — deshalb sah
  // der Befund wie diese Region aus. Die Karte laedt nur beim Einhaengen und
  // blieb nach der Erholung im Fehler; das ist E-8, nicht diese Wache.
  //
  // Diese Wache bleibt: die Ueberholung ist ein moeglicher Fehler DERSELBEN
  // Klasse, im Browser nur nicht beobachtet. Sie ist der Mechanismus aus
  // E-7(a) — nicht die Ursache des Befunds.
  const laufRef = useRef(0);

  const fetchData = useCallback(async () => {
    const lauf = laufRef.current + 1;
    laufRef.current = lauf;
    setLoading(true);
    try {
      // `allSettled`, nicht `all`: ein Ausfall darf die uebrigen sechs
      // Regionen nicht mitreissen. Jede Region bekommt ihr Ergebnis einzeln
      // ausgehaendigt und traegt ihren eigenen Fehler (W-07).
      const results = await Promise.allSettled(
        Object.values(REGION_ENDPOINTS).map((path) => axios.get(withApiBase(path)))
      );

      const failed = {};
      const data = {};
      Object.keys(REGION_ENDPOINTS).forEach((key, i) => {
        const result = results[i];
        if (result.status === 'fulfilled') {
          data[key] = result.value?.data ?? null;
          return;
        }
        failed[key] = true;
        console.error(
          `Dashboard region "${key}" failed (${REGION_ENDPOINTS[key]}):`,
          result.reason
        );
      });

      // Ueberholt: ein juengerer Lauf ist unterwegs oder fertig. Dann NICHTS
      // schreiben — er ist die Wahrheit, nicht dieser hier.
      //
      // 🔴 Die Wache sitzt HINTER der Meldungsschleife (R16-WV1, Nit,
      // Nachbesserung 2): der ueberholte Lauf behaelt seine Diagnosezeile. Die
      // `console.error` sind Protokoll, kein Zustand — sie raeumen nichts weg,
      // was der neuere Lauf gerade geschrieben haette.
      if (lauf !== laufRef.current) return;

      setRegionErrors(failed);

      // Eine ausgefallene Region schreibt ihren VORGABEWERT, nicht ihren
      // letzten Stand: sonst stuenden alte Zahlen unter einem Fehlerhinweis
      // und niemand koennte sagen, welche von beiden gerade gilt.
      setKpis(failed.kpis ? null : data.kpis);
      setRmaStats(failed.rma ? null : data.rma);
      setReorderTasks(failed.reorder ? [] : data.reorder || []);
      setIncomingItems(failed.incoming ? [] : data.incoming || []);
      setStorageSummary(failed.storage ? null : data.storage || null);
      setScraperLastRun(failed.lastRun ? null : data.lastRun || null);
      // W-33/D1: die Uhrzeit der Kopfzeilen-Unterzeile. Sie steht HIER und
      // nicht in einem eigenen Effekt, weil sie genau das datiert, was dieser
      // Aufruf geschrieben hat — eine getrennt gemessene Uhrzeit koennte
      // Sekunden neben den Zahlen liegen, die sie datieren soll.
      setLastLoadedAt(new Date());
    } finally {
      // Auch das Aufraeumen gehoert dem juengsten Lauf: sonst nimmt ein
      // ueberholter Lauf dem laufenden die Ladeanzeige weg (dieselbe Wache).
      if (lauf === laufRef.current) setLoading(false);
    }
  }, []);

  // 🔴 Eigene Lauf-Wache (R16-WV1 W-2, Nachbesserung 2, 2026-10-11). Derselbe
  // Ausloeser wie `fetchData` — `handleRefresh` treibt ueber `refreshTick` BEIDE
  // Effekte —, also dieselbe Ueberholung: der aeltere Lauf schreibt sein
  // Ergebnis NACH dem neueren. Er schreibt hier `setStaleTotal(null)`, und das
  // ist genau der Unbekannt-Zustand („Could not check targets"), den der
  // Erholungslauf gerade weggeraeumt hat.
  //
  // 🔴 Ein EIGENER Zaehler, nicht `laufRef`: teilten sich die zwei Lader einen,
  // entwertete jeder Start des einen den laufenden des anderen — die Wache
  // schluege dann zu, wo gar keine Ueberholung ist.
  const staleLaufRef = useRef(0);

  const fetchStaleTargets = useCallback(async (pageIndex) => {
    const lauf = staleLaufRef.current + 1;
    staleLaufRef.current = lauf;
    setStaleLoading(true);
    try {
      const { data } = await axios.get(withApiBase('/api/dashboard/stale-targets'), {
        params: { limit: STALE_PAGE_SIZE, page: pageIndex },
      });
      if (lauf !== staleLaufRef.current) return;
      setStaleTargets(data?.items || []);
      setStaleTotal(data?.total || 0);
    } catch (err) {
      console.error(err);
      // Ueberholt — dieselbe Wache wie oben, und aus demselben Grund: der
      // Fehler des aelteren Laufs ist nicht der Stand des neueren.
      if (lauf !== staleLaufRef.current) return;
      // 🔴 B1 (Rule-15 gate, 2026-09-19). This wrote `0`, and `0` is a VALUE —
      // it made a failed fetch render "All targets up to date" under a green
      // check. The `?? 0` this branch removed were call-site defaults; this one
      // is written as STATE, which is why removing `?? 0` never reached it.
      // `null` means "we do not know", and the all-clear statements below are
      // gated on knowing.
      //
      // 🔴 The unknown-ness lives in `staleTotal`, NOT in the list (W-06 gate,
      // 2026-09-20). Writing `null` into `staleTargets` as well crashed the
      // whole dashboard: `:280/:281` call `.filter` on it unconditionally, and
      // both are top-level consts above the only `return` in this component, so
      // no early return can skip them. Any rejection of that one endpoint —
      // network, 500, 401, timeout — then took down every tile on the page, not
      // just this one. Same class as the `resolveCTA` crash.
      //
      // The list therefore stays `[]`: it means "no targets to show", which is
      // true. `staleKnown`, derived from `staleTotal`, is what suppresses the
      // all-clear — `null > 0` is already false, so it is the flag and not the
      // comparison that does the work.
      setStaleTargets([]);
      setStaleTotal(null);
    } finally {
      // Auch das Aufraeumen gehoert dem juengsten Lauf — sonst nimmt ein
      // ueberholter Lauf dem laufenden die Ladeanzeige weg (dieselbe Wache).
      if (lauf === staleLaufRef.current) setStaleLoading(false);
    }
  }, []);

  const handleRefresh = useCallback(() => {
    setRefreshTick((t) => t + 1);
  }, []);

  // 🔴 UI2 Teil 9, Karte Z-1c (E-3): nach der Erholung laden die REGIONEN neu —
  // ueber den BESTEHENDEN Ladeweg (`refreshTick` speist beide Effekte unten),
  // kein zweiter. Das heilt auch „Could not check targets" (`fetchStaleTargets`)
  // und die vier Regionen-Alerts; die Seite selbst bleibt stehen (E-1).
  useReloadOnRecovery(handleRefresh);

  const pollScraperStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(withApiBase('/api/scrapers/run/status'));
      setScraperRunStatus(data);
      if (data.running && !syncEmittedRef.current) {
        window.dispatchEvent(new CustomEvent('global-sync', {
          detail: { type: 'start', id: BATCH_REFRESH_SYNC_ID, label: 'Scraping stale targets' },
        }));
        syncEmittedRef.current = true;
      }
      if (!data.running && syncEmittedRef.current && !batchRefreshPending && Date.now() > syncGraceUntilRef.current) {
        window.dispatchEvent(new CustomEvent('global-sync', { detail: { type: 'end', id: BATCH_REFRESH_SYNC_ID } }));
        syncEmittedRef.current = false;
        handleRefresh();
      }
    } catch {
      // ignore poll errors
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchRefreshPending, handleRefresh]); // batchRefreshPending checked inline to decide whether to trigger sync end

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshTick]);

  useEffect(() => {
    fetchStaleTargets(stalePage);
  }, [fetchStaleTargets, stalePage, refreshTick]);

  // Poll scraper run status on mount and every 5s
  useEffect(() => {
    pollScraperStatus();
    const interval = setInterval(pollScraperStatus, 5000);
    return () => clearInterval(interval);
  }, [pollScraperStatus]);

  // Detect when a batch run just finished — capture result stats for feedback
  useEffect(() => {
    const wasRunning = prevBatchRunningRef.current;
    prevBatchRunningRef.current = batchRefreshRunning;
    if (wasRunning && !batchRefreshRunning && scraperRunStatus) {
      const succeeded = scraperRunStatus.succeeded ?? 0;
      const failed = scraperRunStatus.failed ?? 0;
      const total = scraperRunStatus.total ?? (succeeded + failed);
      if (total > 0) {
        setBatchResult({ succeeded, failed, total });
      }
    }
  }, [batchRefreshRunning, scraperRunStatus]);

  useEffect(() => {
    setStalePage((prevPage) => {
      const maxPage = Math.max(0, Math.ceil(staleTotal / STALE_PAGE_SIZE) - 1);
      return prevPage > maxPage ? maxPage : prevPage;
    });
  }, [staleTotal]);

  const handleIncomingEventClick = useCallback(
    (resource) => {
      if (resource?.po_id) navigate(`/purchase_order/${resource.po_id}`);
    },
    [navigate]
  );

  const handleReorderEventClick = useCallback(
    (resource) => {
      if (resource?.sku) navigate(`/products/${resource.sku}`);
    },
    [navigate]
  );

  // Depend on the STABLE stop fn (useCallback([]) in useScrapeStream), NOT the
  // `scrape` object — that object is a fresh literal every render, so [scrape]
  // fires this cleanup on EVERY render and closes the EventSource the same tick
  // start() opens it (aborting the request before it's even sent). Mount/unmount only.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- exhaustive-deps wants [scrape], but scrape is a fresh object each render → that reintroduces the every-render cleanup bug; scrape.stop is stable (useCallback([]))
  useEffect(() => () => scrape.stop(), [scrape.stop]);

  const handleScrapeTarget = useCallback(async (target) => {
    if (!target || activeScrapeTargetId || batchRefreshRunning) return;

    // Set active target synchronously so the trigger button disables before
    // the token-fetch await — prevents double-click during the network hop.
    setActiveScrapeTargetId(target.id);

    // EventSource can't send Authorization headers — fetch SSE token first.
    let sseToken;
    try {
      sseToken = await getScraperSSEToken();
    } catch (err) {
      setActiveScrapeTargetId(null);
      return;
    }

    const params = new URLSearchParams({ target_id: target.id, persist: 'true', token: sseToken });
    if (target.product_url || target.productUrl) {
      params.set('product_url', target.product_url || target.productUrl);
    }
    const url = `${withApiBase('/api/scrapers/config/run')}?${params.toString()}`;

    scrape.start({
      url,
      onClose: () => {
        setActiveScrapeTargetId(null);
        handleRefresh();
      },
    });
  }, [activeScrapeTargetId, batchRefreshRunning, handleRefresh, scrape]);

  const handleBatchRefresh = useCallback(async () => {
    // batchRefreshLockRef guards against re-clicks in the window between this
    // call and React re-rendering with batchRefreshPending=true.
    if (batchRefreshRunning || batchRefreshLockRef.current) return;
    batchRefreshLockRef.current = true; // synchronous — effective immediately

    setBatchRefreshPending(true);
    syncGraceUntilRef.current = Date.now() + 30000;
    if (!syncEmittedRef.current) {
      window.dispatchEvent(new CustomEvent('global-sync', {
        detail: { type: 'start', id: BATCH_REFRESH_SYNC_ID, label: 'Scraping stale targets' },
      }));
      syncEmittedRef.current = true;
    }

    let clearImmediately = false;
    setScrapeError(null);
    setBatchResult(null);
    try {
      await axios.post(withApiBase('/api/dashboard/stale-targets/batch-refresh'));
      // Immediately poll so the UI can pick up running:true before the guard drops
      await pollScraperStatus();
    } catch (err) {
      if (err?.response?.status === 409) {
        // Server says already running — poll to sync state, then let the
        // delayed clear handle clean-up (scraper will be running by next poll)
        await pollScraperStatus();
      } else {
        clearImmediately = true;
        const status = err?.response?.status;
        const msg = !err?.response
          ? 'Cannot reach the server. Check your internet connection and try again.'
          : status === 401 || status === 403
          ? 'Not authorized to run the scraper. The API key may be missing or expired.'
          : status === 500
          ? 'Server error while starting the scraper. The backend may be overloaded — try again in a minute.'
          // UI2 Teil 9, Karte Z-1c (E-6): kein roher Statuscode im SICHTBAREN Satz.
          // Er gehoert ins Protokoll — dort, mit eigenem Argument, ist er fuer die
          // Diagnose da und fuer niemanden sonst sichtbar.
          : 'Could not start scraper. Try again or check the server logs.';
        // Genau EINE Meldung je Fehlschlag (R15-W4/R16-W-3, Nachbesserung 1).
        // Sie traegt den Status UND das Fehlerobjekt — der sichtbare Satz
        // darueber traegt keinen Statuscode (E-6).
        console.error('Scraper start failed', { status }, err);
        setScrapeError(msg);
        syncGraceUntilRef.current = 0;
        if (syncEmittedRef.current) {
          window.dispatchEvent(new CustomEvent('global-sync', { detail: { type: 'end', id: BATCH_REFRESH_SYNC_ID } }));
          syncEmittedRef.current = false;
        }
      }
    } finally {
      if (clearImmediately) {
        // Real error — release the guard immediately so the user can retry
        setBatchRefreshPending(false);
        batchRefreshLockRef.current = false;
      } else {
        // Success / 409 — delay the clear to bridge the 5s polling gap.
        // scraperRunStatus.running will become true within the delay, keeping
        // batchRefreshRunning true even after batchRefreshPending drops.
        setTimeout(() => {
          setBatchRefreshPending(false);
          batchRefreshLockRef.current = false;
        }, 8000);
      }
    }
  }, [batchRefreshRunning, pollScraperStatus]);

  const openTargetMenu = useCallback((event, target) => {
    event.stopPropagation();
    setTargetMenuAnchor(event.currentTarget);
    setTargetMenuTarget(target);
  }, []);

  const closeTargetMenu = useCallback(() => {
    setTargetMenuAnchor(null);
    setTargetMenuTarget(null);
  }, []);

  const handleDeactivateTarget = useCallback(async () => {
    if (!targetMenuTarget) return;
    try {
      await axios.post(withApiBase(`/api/scrapers/config/${targetMenuTarget.id}/deactivate`));
      handleRefresh();
    } catch (err) {
      console.error(err);
      setError('Failed to deactivate target.');
    } finally {
      closeTargetMenu();
    }
  }, [targetMenuTarget, handleRefresh, closeTargetMenu]);

  return (
    // 🔴 G0b.5 (2026-10-13): `maxWidth="xl"` → `width="wide"` (Konzept §5.4:741,
    // Plan §3.3). Der Prop war wirkungslos (der `MuiContainer`-Override zog `xl`
    // per `!important` auf 1440); nach dem Umbau waere er MUI's 1536 geworden —
    // ein zweiter Deckel neben dem Muster. `/` ist WF5 `wide` (§2.2:273).
    // 🔴 `data-testid="liste-panel"` (UI2 Teil 10, G15b, 2026-11-03): die
    //    Hausmarke der Inhaltsflaeche (§15) — Wurzel, unter der die
    //    `SectionBlock`s stehen. `PageContainer` reicht unbekannte Props an den
    //    AEUSSEREN MUI-`Container` durch (`PageContainer.jsx:135`); die Marke
    //    `page-container` sitzt dagegen auf der INNEREN `Box` (`:145`). Das
    //    sind ZWEI Elemente, nicht eins — `liste-panel` ist der Vorfahre von
    //    `page-container`, nicht dasselbe Element (R15 W1, 2026-11-03).
    //    `liste-skelett` gibt es hier NICHT: `/` hat kein Seitengeruest, die
    //    Kacheln skeletonisieren sich selbst — `oeffne()` wartet auf eine
    //    abwesende Marke sofort (`helfer.ts:629-640`).
    <PageContainer width="wide" data-testid="liste-panel">
      {/* 🔴 E8 (2026-10-07) → F3 (2026-10-08): der Refresh stand seit E8 in
          einer EIGENEN Zeile unter dem Kopf (`flex-end`, ohne `isMobile`-Zweig)
          — auf beiden Breiten eine Zeile mit einem Symbolknopf darin (T3-3).
          Er steht wieder im Kopf, diesmal als `iconOnly`-Aktion: der Kopf
          rendert sie als `RefreshButton`, also NIE `contained` — genau der
          Grund, aus dem E8 ihn herausgenommen hatte, ist damit weg.
          🔴 `label` ist der barrierefreie Name (`RefreshButton` wirft ohne).
          🔴 `firstLoad`: die Kacheln skeletonisieren sich im Aufbau selbst
          (`UrgencyRow loading`), der Knopf dreht sich dann nicht mit —
          Anti-Pattern 11. `lastLoadedAt` ist `null`, bis der erste Abruf
          durch ist; danach ist jeder weitere Lauf ein NEULADEN. */}
      <ViewHeader
        title="Home"
        subtitle={fmtDataAsOf(lastLoadedAt)}
        actions={[{
          key: 'refresh',
          label: 'Refresh dashboard',
          iconOnly: true,
          loading,
          firstLoad: loading && !lastLoadedAt,
          onClick: handleRefresh,
        }]}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <BlogBanner apiBase={API_BASE} />

      {/* ── KPI Zone: urgency row ──
          🔴 W-33/D6: kein `{!loading && …}` mehr. Der Streifen bleibt stehen
          und jede Kachel skeletonisiert sich selbst (`loading` +
          `reserveHint`), statt nach dem Laden zu erscheinen und die halbe
          Seite nach unten zu schieben. Der seitenweite `LinearProgress` ueber
          der Seite ist dafuer geloescht: eine Fortschrittsleiste am Kopf sagt
          "irgendetwas laedt", waehrend die Flaeche darunter nicht sagt, WAS —
          die Kachelform sagt beides. */}
      <UrgencyRow
        kpis={kpis}
        rmaStats={rmaStats}
        storageSummary={storageSummary}
        loading={loading}
      />
      {/* Die Kacheln selbst vertragen den Ausfall: `StatTile` zeichnet ein
          fehlendes `value` als "—" (W-06). Was fehlte, war der SATZ dazu —
          ohne ihn sieht "—" aus wie eine Null. */}
      {!loading && kpiErrorSources.length > 0 && (
        /* 🔴 W3/W-3 der Gates zu PR 4: EIN Fehler-Idiom je Fehlerklasse. Hier
           stand `EmptyState reason="error"` — und `EmptyState` zeichnet JEDEN
           Grund gleich (zentriert, ohne Symbol, ohne Farbe), also war "Fehler
           ist nicht leer" nur im Text wahr, nicht auf den ersten Blick. Das
           Konzept §4.1 schreibt fuer Regionsfehler `<Alert severity="error"
           action={<Retry/>}>` vor, und der Lager-Abschnitt derselben Seite
           macht es bereits so. */
        <Alert
          severity="error"
          sx={{ mt: 1 }}
          action={<RetryAction onClick={handleRefresh} />}
        >
          Part of the KPIs could not be loaded.
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            No data from {kpiErrorSources.map((key) => `GET ${REGION_ENDPOINTS[key]}`).join(', ')}
          </Typography>
        </Alert>
      )}

      {/* ── Scraper Targets ── */}
      <Box sx={{ mt: 3 }}>
        {/* Section title outside the body — design system convention */}
        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
          <Typography variant="h6">Scraper targets</Typography>
          {/* W-33/D2: DIES ist die eine Stelle, an der "39 veraltete Ziele"
              stehen bleibt. Die Kachel im Streifen darueber ist geloescht.
              W-33/D8: `Pill` statt `Chip` — der Abschnittskopf traegt die Zahl
              als Zaehl-Pille, nicht als MUI-Chip mit eigener Farblogik. */}
          {hasStaleTargets && (
            <Pill tone={errorTargets.length > 0 ? 'err' : 'warn'} count={staleTotal} />
          )}
        </Stack>

        <Panel pad="none" clip>
          {/* 🔴 W-33/D8: der Farbverlauf ist weg. `linear-gradient(135deg,
              #667eea, #764ba2)` trug keine Information, die die Zaehl-Pille im
              Abschnittskopf darueber nicht schon traegt — er machte die Karte
              wichtig, und genau das leistet `PANEL_SX` plus die Zahl. Mit ihm
              gehen die `color: 'white'`-Ueberschreibungen an Knopf und Symbol:
              sie waren nur noetig, weil der Grund violett war.

              Klickbar zum Aufklappen bleibt die Zeile — das ist eine
              Bedienung, keine Zierde. */}
          <Box
            onClick={() => setScraperExpanded(v => !v)}
            sx={{
              p: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReplayIcon />
              <Typography variant="subtitle2" fontWeight={600}>
                {batchRefreshRunning
                  ? 'Scraping in progress…'
                  : hasStaleTargets
                  ? `${staleTotal} stale${errorTargets.length > 0 ? ` · ${errorTargets.length} with errors` : ''}`
                  : staleKnown
                  ? 'All targets up to date'
                  : 'Could not check targets'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary">
                Last scraped: {scraperRunStatus?.last_run_at ? formatRelativeHours(scraperRunStatus.last_run_at) : '—'}
              </Typography>
              {hasStaleTargets && (
                <DisabledReason
                  reason={batchRefreshRunning
                    ? null
                    : activeScrapeTargetId
                      ? 'Wait for the running scrape to finish first.'
                      : null}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={batchRefreshRunning
                      ? <CircularProgress size={13} color="inherit" />
                      : <ReplayIcon fontSize="small" />}
                    disabled={batchRefreshRunning || Boolean(activeScrapeTargetId)}
                    onClick={(e) => { e.stopPropagation(); handleBatchRefresh(); }}
                    sx={{ fontWeight: 600, py: 0.5 }}
                  >
                    {batchRefreshRunning ? 'Scraping…' : 'Refresh all'}
                  </Button>
                </DisabledReason>
              )}
              <IconButton
                size="small"
                aria-label={`${scraperExpanded ? 'Collapse' : 'Expand'} scrapers`}
                onClick={(e) => { e.stopPropagation(); setScraperExpanded(v => !v); }}
              >
                {scraperExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Box>
          </Box>

          {/* Expandable content panel */}
          <Collapse in={scraperExpanded}>
            <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
              {/* Data freshness info — last scrape timestamp + next scheduled run (CR-007, CR-008) */}
              {/* 🔴 Eigener Fehlerhinweis statt eines stillen Nichts (W-07). Bis
                  hierher verschluckte `.catch(() => ({ data: null }))` den
                  Ausfall, und die Zeile verschwand — "kein letzter Lauf
                  bekannt" sah aus wie "noch nie gelaufen". Bewusst eine
                  Caption und kein `EmptyState`: hier steht eine zweizeilige
                  Fussnote im aufgeklappten Panel, keine eigene Flaeche mit
                  Leerzustand. */}
              {/* 🔴 E-5 (Z-1c): im bestaetigten Ausfall schweigt dieser rote
                  Klartext wie ein `MuiAlert` unter E-3 — er ist KEIN Alert, die
                  Theme-Regel erreicht ihn also nicht. Nach der Erholung steht er
                  wieder da, falls der Abruf weiter scheitert; bleibt der Fehler
                  aus, nimmt ihn der Neuabruf mit. */}
              {regionErrors.lastRun && !imAusfall ? (
                <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: SP.sm }}>
                  Last run could not be loaded (GET {REGION_ENDPOINTS.lastRun}).
                </Typography>
              ) : scraperLastRun && (scraperLastRun.last_scraped_at || scraperLastRun.next_run_at) && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                  {scraperLastRun.last_scraped_at && (
                    <Typography variant="caption" color="text.secondary">
                      Last scrape: <strong>{formatDateTime(scraperLastRun.last_scraped_at)}</strong>
                    </Typography>
                  )}
                  {scraperLastRun.next_run_at && (
                    <Typography variant="caption" color="text.secondary">
                      Next: <strong>{formatDateTime(scraperLastRun.next_run_at)}</strong>
                    </Typography>
                  )}
                </Box>
              )}

              {/* Progress / status / error */}
              {(batchRefreshRunning || activeScrapeTargetId || batchResult || scrapeError) && (
                <Box sx={{
                  mb: 2, p: 1.5, borderRadius: 1.5, bgcolor: 'action.hover',
                  border: '1px solid',
                  borderColor: scrapeError ? 'error.light' : batchResult?.failed > 0 ? 'warning.light' : 'divider',
                }}>
                  {batchRefreshRunning && (() => {
                    const prog = scraperRunStatus;
                    const total = prog?.total || 0;
                    const completed = prog?.completed || 0;
                    const succeeded = prog?.succeeded || 0;
                    const failed = prog?.failed || 0;
                    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
                    return (
                      <Stack spacing={0.75}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Typography variant="caption" fontWeight={600}>
                            {total > 0 ? `${completed} / ${total} targets` : 'Starting…'}
                          </Typography>
                          {total > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {succeeded > 0 && `${succeeded} ok`}
                              {succeeded > 0 && failed > 0 && ' · '}
                              {failed > 0 && <Box component="span" sx={{ color: 'error.main' }}>{failed} failed</Box>}
                            </Typography>
                          )}
                        </Stack>
                        <LinearProgress variant={total > 0 ? 'determinate' : 'indeterminate'} value={pct} sx={{ borderRadius: 1, height: 6 }} />
                      </Stack>
                    );
                  })()}
                  {activeScrapeTargetId && (
                    <ProgressStream
                      {...scrape.state}
                      title={`Scraping ${staleTargets.find(t => t.id === activeScrapeTargetId)?.product_name || 'target'}`}
                    />
                  )}
                  {batchResult && !batchRefreshRunning && (
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="caption" fontWeight={600} color={batchResult.failed === 0 ? 'success.main' : 'warning.main'} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        {batchResult.failed === 0
                          ? <><Check size={13} />All {batchResult.succeeded} targets refreshed</>
                          : <><AlertTriangle size={13} />{batchResult.succeeded} ok · {batchResult.failed} failed</>}
                      </Typography>
                      <Button size="small" sx={{ fontSize: 11, py: 0 }} onClick={() => setBatchResult(null)}>Dismiss</Button>
                    </Stack>
                  )}
                  {/* 🔴 E-5 (Z-1c): dieselbe Ausblendung wie oben — auch dieser
                      rote Klartext ist kein `MuiAlert`. */}
                  {scrapeError && !imAusfall && (
                    <Stack direction="row" alignItems="flex-start" spacing={1}>
                      <Typography variant="caption" color="error.main" sx={{ flex: 1 }}>{scrapeError}</Typography>
                      <Button size="small" sx={{ fontSize: 11, py: 0, flexShrink: 0 }} onClick={() => setScrapeError(null)}>Dismiss</Button>
                    </Stack>
                  )}
                </Box>
              )}

              {/* All clear */}
              {staleKnown && !hasStaleTargets && !staleLoading && (
                <Typography variant="body2" color="success.main" sx={{ py: 0.5, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <Check size={13} />All scraper targets are up to date
                </Typography>
              )}

              {/* Issues group */}
              {errorTargets.length > 0 && (
                <Box>
                  <Typography variant="overline" sx={{ color: 'error.main', fontSize: 10, fontWeight: 700, letterSpacing: 1, display: 'block', mb: 0.5 }}>
                    Issues — {errorTargets.length} target{errorTargets.length !== 1 ? 's' : ''} with repeated errors
                  </Typography>
                  {errorTargets.map(target => (
                    <StaleTargetRow
                      key={target.id}
                      target={target}
                      activeScrapeTargetId={activeScrapeTargetId}
                      batchRefreshRunning={batchRefreshRunning}
                      onScrape={handleScrapeTarget}
                      onOpenMenu={openTargetMenu}
                    />
                  ))}
                </Box>
              )}

              {errorTargets.length > 0 && staleHealthyTargets.length > 0 && <Divider sx={{ my: 1.5 }} />}

              {/* Stale (no errors) group */}
              {staleHealthyTargets.length > 0 && (
                <Box>
                  <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: 10, fontWeight: 700, letterSpacing: 1, display: 'block', mb: 0.5 }}>
                    Stale — {staleHealthyTargets.length}{staleTotal > staleTargets.length ? ` of ${staleTotal}` : ''} target{staleHealthyTargets.length !== 1 ? 's' : ''} not scraped in 24h
                  </Typography>
                  {staleHealthyTargets.map(target => (
                    <StaleTargetRow
                      key={target.id}
                      target={target}
                      activeScrapeTargetId={activeScrapeTargetId}
                      batchRefreshRunning={batchRefreshRunning}
                      onScrape={handleScrapeTarget}
                      onOpenMenu={openTargetMenu}
                    />
                  ))}
                </Box>
              )}

              {staleTotal > staleTargets.length && (
                <Button size="small" component={RouterLink} to="/scrapers" sx={{ mt: 1.5 }}>
                  View all {staleTotal} stale targets →
                </Button>
              )}
            </Box>
          </Collapse>
        </Panel>
      </Box>

      {/* Context menu for stale targets */}
      <Menu
        anchorEl={targetMenuAnchor}
        open={Boolean(targetMenuAnchor && targetMenuTarget)}
        onClose={closeTargetMenu}
      >
        <MenuItem component={RouterLink} to={targetMenuTarget ? `/scrapers/${targetMenuTarget.id}/edit` : '#'} onClick={closeTargetMenu}>
          Open scraper config
        </MenuItem>
        <MenuItem onClick={handleDeactivateTarget}>Deactivate target</MenuItem>
        {targetMenuTarget?.product_url && (
          <MenuItem component="a" href={targetMenuTarget.product_url} target="_blank" rel="noreferrer">
            Open target URL
          </MenuItem>
        )}
      </Menu>

      {/* 🔴 W-33/D11: die Reihenfolge ist die der Handlung, nicht die der
          Datenmenge. "Manual price drift" stand unter "Pricing automation",
          obwohl es die kleinere und die von Hand zu entscheidende Flaeche ist;
          "Market Intelligence" stand ganz oben, obwohl es ein Lesefluss ohne
          eine einzige Aktion ist. Jetzt: Streifen → Scraper-Ziele → manueller
          Preis-Drift → Preisautomatik → Marktlage → Lager. */}

      {/* ── Manual Price Drift ──
          🔴 F8 (2026-10-08) war der falsche Weg, Nachbesserung 2 dreht ihn um
          (Orchestrator-Entscheid zur R16-Auflage B1): der Abschnitts-`h6` steht
          wieder HIER, ueber der Karte — und der Name in der Karte faellt.
          Beides zusammen loest T3-9 („derselbe Text zweimal untereinander");
          die erste Fassung loeste es von der falschen Seite und stellte dabei
          die Abschnittshierarchie der Seite auf zwei Groessen: die vier
          uebrigen Abschnitte tragen `h6` in 1,25 rem, die zwei Preis-Karten
          haetten ihren Namen in 0,875 rem getragen (`subtitle2`), bei GLEICHER
          Dokumentstufe — sie laesen als Unterpunkte der anderen.
          🔴 Die Hausform ist die von „Scraper targets" (`:780-790`) und
          `SectionBlock` (`:1416`): Ueberschrift UEBER der Karte, die Karte
          selbst traegt nur Symbol, Chips und ihre Statuszeile. Kein
          `aria-labelledby`: die Hausform fuer „Ueberschrift + Flaeche" ist auf
          dieser Seite an vier Stellen dieselbe und benutzt es nirgends. */}
      <Box sx={{ mt: 3 }}>
        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
          <Typography variant="h6">Manual price drift</Typography>
        </Stack>
        <ManualDriftCard />
      </Box>

      {/* ── Pricing Automation ── (Nachbesserung 2, wie oben: `h6` ueber der
          Karte statt eines Kartentitels) */}
      <Box sx={{ mt: 3 }}>
        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
          <Typography variant="h6">Pricing automation</Typography>
        </Stack>
        <PricingAutomationDashboard />
      </Box>

      {/* ── Market Intelligence ──
          Ein Lesefluss ohne Aktion, deshalb unter den beiden Flaechen mit
          lebenden Zahlen (39 veraltete Ziele, N driftende Preise). */}
      <Box sx={{ mt: 3 }}>
        <MarketIntelligenceWidget />
      </Box>

      {/* ── Inbound & Storage grid ── */}
      <Box
        sx={{
          mt: 3,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
          gap: 3,
        }}
      >
        <SectionBlock title="Inbound & outbound summary">
          {calendarErrorSources.length > 0 ? (
            /* W3/W-3: dieselbe Form wie der KPI-Streifen und der Lager-Abschnitt
               — `Alert severity="error"` mit Wiederholen, nicht `EmptyState`.
               `EmptyState reason="error"` sah hier aus wie "der Kalender ist
               leer", und das ist die eine Verwechslung, die §4.1 verbietet. */
            <Alert
              severity="error"
              action={<RetryAction onClick={handleRefresh} />}
            >
              The calendar could not be loaded.
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                No data from {calendarErrorSources.map((key) => `GET ${REGION_ENDPOINTS[key]}`).join(', ')}
              </Typography>
            </Alert>
          ) : calendarIncomingEvents.length || calendarReorderEvents.length ? (
            <WeekCalendarView
              incomingEvents={calendarIncomingEvents}
              reorderEvents={calendarReorderEvents}
              onIncomingClick={handleIncomingEventClick}
              onReorderClick={handleReorderEventClick}
            />
          ) : (
            /* 🔴 W-33/D9: kein `PanelPlaceholder` mehr. Dessen drei graue
               Balken waren die Zeichnung eines Ladezustands an einer Stelle,
               an der nichts laedt — der Abruf ist durch, das Ergebnis ist
               leer. Und der feste Schluss-Satz "Looks like everything is done
               for today 🎉" behauptete eine Ursache, die dieser Zweig nicht
               kennt: er sieht einen leeren Kalender, nicht eine erledigte
               Arbeit. Ein leeres Brett ist eine Auskunft, kein Erfolg.

               🔴 P9 (UI2 Teil 10, G15b, 2026-11-03): `EmptyState` statt
               `Alert severity="info"`. Ein `Alert` traegt kein `[data-reason]`,
               und P9 verlangt den Grund im DOM. Der Wortlaut bleibt woertlich.
               `reason="no-data"` — nicht `no-match`: diese Seite hat keine
               Suche und keinen Filter, „kein Treffer" waere falsch (N8.2). */
            <EmptyState reason="no-data" message="Nothing scheduled for this week." />
          )}
        </SectionBlock>

        <SectionBlock title="Storage and inventory">
          {!storageSummary ? (
            /* 🔴 W-33/D9: hier stand `PanelPlaceholder message="Storage data
               pending"`. "Pending" war eine Behauptung ueber die Zukunft, die
               dieser Zweig nicht belegen kann — er kennt nur `null`, und
               `null` entsteht auf ZWEI Wegen: der Abruf ist gescheitert, oder
               er kam ohne Inhalt zurueck. Beide sind Ausfaelle, nicht Warten.
               Ein Fehlerhinweis mit Wiederholen ist die einzige Form, die
               beide Faelle richtig beschreibt.

               W-33/D12: die Zeile "Total open purchase orders" in diesem Panel
               ist geloescht — dieselbe Zahl stand als Kachel "Open POs" im
               Streifen darueber, mit demselben Ziel `/overview`. */
            <Alert
              severity="error"
              action={<RetryAction onClick={handleRefresh} />}
            >
              Storage and inventory could not be loaded.
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                No data from GET {REGION_ENDPOINTS.storage}
              </Typography>
            </Alert>
          ) : (
            <Stack spacing={2} sx={{ width: '100%' }}>
              <MetricRow
                label="Incoming inventory amount"
                value={money('EUR', storageSummary?.incoming_inventory_amount)}
                to="/incoming-inventory"
                infoTooltip={HOME_DASHBOARD_TOOLTIPS.incoming_inventory_amount}
              />
              <MetricRow
                label="Current stock value"
                value={money('EUR', storageSummary?.current_stock_value)}
                infoTooltip={HOME_DASHBOARD_TOOLTIPS.current_stock_value}
              />
              <MetricRow
                label="Total sales (30d)"
                value={money('EUR', storageSummary?.sales_revenue)}
                to="/products"
                infoTooltip={HOME_DASHBOARD_TOOLTIPS.sales_revenue}
                hint={
                  storageSummary.sales_change_percent != null
                    ? `${storageSummary.sales_change_percent >= 0 ? '+' : ''}${formatPercent(storageSummary.sales_change_percent / 100, { digits: 1 })} vs. previous 30d`
                    : 'No change data'
                }
                accent={
                  storageSummary.sales_change_percent > 0
                    ? 'success.main'
                    : storageSummary.sales_change_percent < 0
                    ? 'error.main'
                    : 'text.secondary'
                }
              />
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ width: '100%' }}
              >
                <Box
                  component={RouterLink}
                  to="/fulfillment"
                  sx={{
                    textDecoration: 'none',
                    color: 'inherit',
                    display: 'block',
                    flex: 1,
                    minWidth: 0,
                    borderRadius: 2,
                    px: 2,
                    py: 1.5,
                    ...hoverOnly({ backgroundColor: 'action.hover' }),
                  }}
                >
                  <FulfillmentCostKpiCard
                    kind="DN"
                    data={storageSummary.fulfillment_cost_rolling?.dn}
                    loading={false}
                    error={storageSummary.fulfillment_cost_error}
                  />
                </Box>
                <Box
                  component={RouterLink}
                  to="/fulfillment"
                  sx={{
                    textDecoration: 'none',
                    color: 'inherit',
                    display: 'block',
                    flex: 1,
                    minWidth: 0,
                    borderRadius: 2,
                    px: 2,
                    py: 1.5,
                    ...hoverOnly({ backgroundColor: 'action.hover' }),
                  }}
                >
                  <FulfillmentCostKpiCard
                    kind="RT"
                    data={storageSummary.fulfillment_cost_rolling?.rt}
                    loading={false}
                    error={storageSummary.fulfillment_cost_error}
                  />
                </Box>
              </Stack>
            </Stack>
          )}
        </SectionBlock>
      </Box>

      {/* ── Done items ── */}
      {groupedDoneEntries.size > 0 && (
        <Box sx={{ mt: 4 }}>
          <Button
            size="small"
            startIcon={<DoneAllIcon />}
            onClick={() => setShowDone((v) => !v)}
          >
            {showDone ? 'Hide' : 'Show'} done items ({doneEntries.length})
          </Button>
          <Collapse in={showDone} unmountOnExit>
            <Panel sx={{ p: { xs: 2, sm: 2.5 }, minHeight: { xs: 'auto', sm: 240 }, display: 'flex', flexDirection: 'column', mt: 2 }}>
              {Array.from(groupedDoneEntries.entries()).map(([type, entries]) => (
                <Box key={type} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'capitalize', mb: 0.5 }}>
                    {type}
                  </Typography>
                  <List dense>
                    {entries.map((entry) => (
                      <ListItem key={entry.key}>
                        <ListItemText
                          primary={entry.label}
                          secondary={
                            entry.completed_at
                              ? `Completed ${formatRelativeHours(entry.completed_at)}`
                              : undefined
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ))}
            </Panel>
          </Collapse>
        </Box>
      )}
    </PageContainer>
  );
}

/* 🔴 Die zwei Waehrungen stehen im Grund selbst (Regel 16 B1): die Fetcher setzen
 * `storefront_currency_mismatch:CAD!=EUR` (`storefront_fetcher.py:73`),
 * `storefront_cart_currency_mismatch:…` (`storefront_cart_fetcher.py:83`) und
 * `shopify_currency_mismatch:…` (`shopify_cart_fetcher.py:213`) — dasselbe
 * `:GOT!=EXPECTED`-Suffix an allen drei Praefixen.
 *
 * 🔴 Der Hinweis nennt damit die BEOBACHTUNG, keine Handlung: Wettbewerbswaehrung
 * und Land sind in der Oberflaeche gar nicht setzbar (`CompetitorDetail.jsx` hat
 * kein Feld dafuer, `ScraperConfigForm` auch nicht) — ein Hinweis, der zu einem
 * Feld schickt, das es nicht gibt, ist schlimmer als keiner (Plan §7.8).
 *
 * Reine Funktion, damit der Test sie ohne Rendern messen kann. `null` heisst
 * „nicht parsebar" und fuehrt auf den allgemeinen Satz — kein Raten.
 */
export function parseCurrencyMismatch(reason) {
  const treffer = /currency_mismatch:([A-Za-z]{3})!=([A-Za-z]{3})\b/.exec(reason || '');
  if (!treffer) return null;
  return { got: treffer[1].toUpperCase(), expected: treffer[2].toUpperCase() };
}

export function classifyScraperError(reason) {
  if (!reason) return null;
  const r = reason.toLowerCase();
  // 🔴 A1 (Plan 2026-10-31): ein Waehrungsbefund ist ein BENANNTER Zustand, nicht
  // „Failed". Die drei Praefixe sind dieselben, aus denen der Scraper den Befund
  // setzt (`WAEHRUNGSBEFUND_PRAEFIXE` in `backend/scrapers/common.py`); bewusst
  // als Liste HIER, weil das Frontend die Python-Konstante nicht importieren
  // kann — die Kopplung haelt `test_health_probe_kennt_alle_praefixe` auf der
  // Serverseite und der Jest-Fall unten auf dieser Seite.
  //
  // ⚠️ Die Pruefung steht VOR den uebrigen Zweigen: „no valid price" und
  // „all scraping techniques failed" koennten den Grund sonst schlucken.
  if (r.includes('currency_mismatch')) {
    // 🔴 Beobachtung, keine Handlung (Regel 16 B1). Mit parsebarem Suffix nennt
    // der Satz die beiden Waehrungen — das ist die Auskunft, die der Mensch
    // braucht; „check the … setting" schickte ihn zu einem Feld, das es nicht gibt.
    const mismatch = parseCurrencyMismatch(reason);
    return {
      label: 'Currency',
      hint: mismatch
        ? `Competitor shop returned ${mismatch.got}, expected ${mismatch.expected} — price skipped.`
        : 'Competitor price came back in another currency — skipped.',
      color: 'warning',
    };
  }
  if (r.includes('timeout') || r.includes('timed out'))
    return { label: 'Timeout', hint: 'Increase timeout or switch scraper type to Selenium', color: 'warning' };
  if (r.includes('404') || r.includes('not found') || r.includes('no such page'))
    return { label: 'URL 404', hint: 'Product URL is broken — update it in the scraper config', color: 'error' };
  if (r.includes('403') || r.includes('forbidden') || r.includes('blocked') || r.includes('access denied') || r.includes('captcha'))
    return { label: 'Blocked', hint: 'Site is blocking the scraper — switch to Selenium stealth', color: 'error' };
  if (r.includes('ssl') || r.includes('certificate'))
    return { label: 'SSL error', hint: 'Enable "Ignore HTTPS errors" in scraper config', color: 'warning' };
  if (r.includes('no valid price') || r.includes('essential fields missing') || r.includes('all scraping techniques failed'))
    return { label: 'No data', hint: 'Selectors returned nothing — review selector config', color: 'warning' };
  if (r.includes('persistence_error') || r.includes('database'))
    return { label: 'DB error', hint: 'Transient database error — will auto-retry', color: 'default' };
  if (r.includes('per_target_timeout'))
    return { label: 'Timeout', hint: 'Target exceeded per-scrape time limit — check URL or increase SCRAPE_TARGET_TIMEOUT_SECONDS', color: 'warning' };
  return { label: 'Failed', hint: reason, color: 'error' };
}

export function StaleTargetRow({ target, activeScrapeTargetId, batchRefreshRunning, onScrape, onOpenMenu }) {
  const failCount = target.failed_scrapes_in_a_row || 0;
  const isScraping = activeScrapeTargetId === target.id;
  const accentColor = failCount >= 5 ? 'error.main' : failCount > 0 ? 'warning.main' : 'text.disabled';
  const errorClass = classifyScraperError(target.last_error);

  // 🔴 Zwei Wege, EIN Knopf. MIT Grund traegt der Baustein den Tooltip (der
  // Knopf ist gesperrt und damit nicht fokussierbar — der Halter macht den
  // Grund tastaturerreichbar). OHNE Grund ist der Knopf AKTIV und bekommt den
  // sichtbaren Hinweis zurueck; der Baustein allein liesse ihn ohne jede
  // Sprechblase.
  //
  // 🔴 Jeder GESPERRTE Zustand nennt seinen Grund — einen dritten, nackten Weg
  // gibt es nicht. Laeuft der Sammel-Refresh, wartet der Knopf mit demselben
  // Satz, den die Datei fuer einen laufenden Scrape schon fuehrt
  // („Wait for the running scrape to finish first.", der Knopf darueber);
  // laeuft ein anderer Einzel-Scrape, sagt er es. `scrapeDisabled` und
  // `scrapeReason` sind damit deckungsgleich: kein gesperrter Knopf bleibt
  // ohne Grund stehen, wenn er kein sichtbares Ladezeichen traegt. Ein `Tooltip` um ein
  // gesperrtes `button`-Kind ginge nie auf (MUI warnt ausdruecklich: „A
  // disabled element does not fire events") — den Grund traegt deshalb der
  // Baustein, nicht ein nackter Knopf.
  //
  // 🔴 Der AKTIVE Zustand (`isScraping`) sagt „Refresh this target": der Ring
  // im Knopf zeigt den laufenden Vorgang, der Tooltip die Handlung.
  const scrapeDisabled = batchRefreshRunning || Boolean(activeScrapeTargetId && !isScraping);
  const scrapeReason = batchRefreshRunning
    ? 'Wait for the running scrape to finish first.'
    : activeScrapeTargetId && !isScraping
      ? 'Another scrape is running — wait for it to finish.'
      : null;
  const scrapeButton = (
    <IconButton
      size="small"
      disabled={scrapeDisabled}
      onClick={() => onScrape(target)}
      aria-label={`Scrape ${target.competitor_name || 'target'}`}
    >
      {isScraping ? <CircularProgress size={14} /> : <ReplayIcon sx={{ fontSize: 18 }} />}
    </IconButton>
  );

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      gap={1.5}
      sx={{
        py: 0.875,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
        minWidth: 0,
      }}
    >
      <Box sx={{
        width: 7, height: 7, borderRadius: '50%', flexShrink: 0, mt: '5px',
        bgcolor: failCount >= 5 ? 'error.main' : failCount > 0 ? 'warning.main' : 'text.disabled',
      }} />
      {/* Clickable content area — navigates to scraper config */}
      <Box
        component={RouterLink}
        to={`/scrapers/${target.id}/edit`}
        sx={{
          flex: 1, minWidth: 0,
          textDecoration: 'none', color: 'inherit',
          borderRadius: 1,
          transition: 'opacity 0.15s',
          // K-5 (Welle 2): reine Wirkung (Deckkraft) ohne Hintergrund.
          ...hoverOnly({ opacity: 0.75 }),
        }}
      >
        <Stack direction="row" alignItems="baseline" gap={1} sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: { xs: 140, sm: 280 } }}>
            {target.product_name || target.sku}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
            {target.competitor_name}
          </Typography>
        </Stack>
        <Typography variant="caption" color={accentColor} sx={{ display: 'block' }}>
          {failCount > 0
            ? `${failCount}× failed · ${formatRelativeHours(target.last_scraped_at)}`
            : formatRelativeHours(target.last_scraped_at)}
        </Typography>
        {errorClass && (
          <Stack direction="row" alignItems="center" gap={0.75} sx={{ mt: 0.25 }}>
            <Tooltip title={`${errorClass.hint}${target.last_error ? `\n\nRaw: ${target.last_error}` : ''}`} arrow placement="bottom-start">
              <Chip
                label={errorClass.label}
                size="small"
                color={errorClass.color}
                variant="outlined"
                sx={{ height: 16, fontSize: 10, fontWeight: 700, cursor: 'help', '& .MuiChip-label': { px: 0.75 } }}
              />
            </Tooltip>
            <Typography
              variant="caption"
              color="text.disabled"
              noWrap
              sx={{ fontSize: 11, maxWidth: { xs: 120, sm: 260 } }}
            >
              {errorClass.hint}
            </Typography>
          </Stack>
        )}
      </Box>
      <Stack direction="row" alignItems="center" gap={0.5} sx={{ flexShrink: 0 }}>
        {scrapeReason ? (
          <DisabledReason reason={scrapeReason}>{scrapeButton}</DisabledReason>
        ) : (
          <Tooltip title="Refresh this target" arrow describeChild>{scrapeButton}</Tooltip>
        )}
        <IconButton
          size="small"
          onClick={(e) => onOpenMenu(e, target)}
          aria-label="Scraper target actions"
        >
          <MoreVertIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Stack>
    </Stack>
  );
}

function SectionBlock({ title, actions = null, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1 }}
      >
        <Typography variant="h6">{title}</Typography>
        {actions && <Box sx={{ flexShrink: 0 }}>{actions}</Box>}
      </Stack>
      <Panel sx={{ p: { xs: 2, sm: 2.5 }, minHeight: { xs: 'auto', sm: 240 }, display: 'flex', flexDirection: 'column' }}>{children}</Panel>
    </Box>
  );
}

/**
 * RetryAction — der Weg aus einem Regionsfehler heraus (W-07).
 *
 * Gehoert in den `action`-Slot eines Fehlerhinweises — seit W3/W-3 der Gates zu
 * PR 4 in den von `Alert severity="error"`, wie es das Konzept §4.1 fuer
 * Regionsfehler vorschreibt. Ein Fehlerhinweis ohne Wiederholen zwingt den
 * Nutzer zum Neuladen der ganzen Seite, obwohl nur ein Abruf schiefging.
 */
function RetryAction({ onClick }) {
  return (
    <Button
      size="small"
      variant="outlined"
      onClick={onClick}
      sx={{ borderRadius: `${BUTTON_RADIUS}px` }}
    >
      Try again
    </Button>
  );
}

function MetricRow({ label, value, hint, accent, to, infoTooltip }) {
  const labelNode = infoTooltip ? (
    <Tooltip title={infoTooltip} arrow placement="top-start">
      <Typography variant="body2" color="text.secondary" sx={{ cursor: 'help' }}>
        {label}
      </Typography>
    </Tooltip>
  ) : (
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
  );

  const content = (
    <Box
      sx={{
        borderRadius: 2,
        px: 2,
        py: 1,
        width: '100%',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ width: '100%' }}>
        {labelNode}
        <Stack alignItems="flex-end">
          <Typography variant="h6" color={accent || 'text.primary'}>
            {/* 🔴 B1 (Rule-16 gate, 2026-09-19). `StatTile` renders
                `value ?? '—'`; this one rendered the value raw, so removing
                `?? 0` at its four call sites would have made them go BLANK
                rather than show the unknown mark. Four edits that could not
                fail are four edits that did nothing. */}
            {value ?? '—'}
          </Typography>
          {hint && (
            <Typography variant="caption" color="text.secondary">
              {hint}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Box>
  );

  if (!to) return content;

  return (
    <Box
      component={RouterLink}
      to={to}
      sx={{
        textDecoration: 'none',
        color: 'inherit',
        display: 'block',
        width: '100%',
        borderRadius: 2,
        transition: 'background-color 0.15s, transform 0.2s ease, box-shadow 0.2s ease',
        ...hoverOnly({ bgcolor: 'action.hover', transform: 'translateY(-1px)' }),
      }}
    >
      {content}
    </Box>
  );
}
